import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import type { ReactNode } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
}

const centered: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  background: 'var(--color-bg)',
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { authStatus, authMessage } = useAuth()
  const location = useLocation()

  if (authStatus === 'loading') {
    return (
      <div style={centered}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  // Valid ibase user, but no Twasta tenant/user provisioned. This is NOT a
  // login problem, so don't bounce to the login page — show a clear message.
  if (authStatus === 'not_provisioned') {
    return (
      <div style={{ ...centered, flexDirection: 'column', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--color-text)' }}>Account not provisioned</h2>
        <p style={{ color: 'var(--color-text-secondary)', maxWidth: 480 }}>
          You're signed in, but this account can't be used with Twasta yet.
          Please contact your administrator to be granted access.
        </p>
        {/* The backend's own reason — an unmapped enterprise, a deactivated
            account and an email bound to another tenant all land here, and
            each needs a different fix. Showing which one turns "contact your
            administrator" into something the administrator can act on. */}
        {authMessage && (
          <p style={{ color: 'var(--color-text-secondary)', maxWidth: 480, fontSize: 14 }}>
            Reason: <strong style={{ color: 'var(--color-text)' }}>{authMessage}</strong>
          </p>
        )}
      </div>
    )
  }

  if (authStatus !== 'authed') {
    // Preserve where the user was headed so the login page (which honors
    // `?next=`) returns them there after signing in, instead of /workbench.
    const dest = `${location.pathname}${location.search}`
    const to = dest && dest !== '/'
      ? `/?next=${encodeURIComponent(dest)}`
      : '/'
    return <Navigate to={to} replace />
  }

  return <>{children}</>
}
